/****************************************************
 * Module Name:  bucketTransactions.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { LoadingButton } from "@mui/lab";
import React, { useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Select from "react-select";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import DashboardProxy from "../../utility/proxy/manager/dashboardProxy";
import Constants from "../../utility/constants";
import { SearchOutlined } from "@mui/icons-material";
import { GrTransaction } from "react-icons/gr";
import CommonSnackbar from "../common/commonSnackBar";
import MixBucketProxy from "../../utility/proxy/manager/mixProxy/mixBucketProxy";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";

const fullOptionToValue = (selectedData) => {
  if (selectedData.value === "*") return [];
  let newArr = [];
  selectedData.map((option) => {
    newArr.push(option.value);
    return true;
  });
  return newArr.includes("*") ? [] : newArr;
};

export default function BucketTransactions() {
  const [chartData, setChartData] = useState([]);
  const [bucketOptions, setBucketOptions] = useState([]);
  const [bucket, setBucket] = useState([]);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [fromDate, setFromDate] = useState(new Date());
  const [bucketIds, setBucketIds] = useState([]);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  const getAllBucketData = () => {
    MixBucketProxy.GetAllBucketDetailMini((response) => {
      setAllBucketData(response);
    });
  };

  const setAllBucketData = (response) => {
    let optionsArray = [];
    optionsArray.push({ value: "*", label: "-- ANY --" });
    response.map((data) => {
      let option = {
        value: data.id,
        label:
          data.stonetypefullname +
          " | " +
          data.shape +
          " | " +
          data.size +
          " | " +
          data.color +
          " | " +
          data.clarity,
      };
      optionsArray.push(option);
      return true;
    });
    setBucketOptions(optionsArray);
    setBucket(optionsArray.length > 0 ? [optionsArray[0]] : bucket);

    //  setFetchData(true);
  };
  useEffect(() => {
    getAllBucketData();
  }, []);

  useEffect(() => {
    handleSearch();
  }, []);

  function groupByKey(array, key) {
    return array.reduce((hash, obj) => {
      if (obj[key] === undefined) return hash;
      return Object.assign(hash, {
        [obj[key]]: (hash[obj[key]] || []).concat(obj),
      });
    }, {});
  }

  const handleResponse = (response) => {
    let buckets = [];
    let transactionArr = [];

    response.buckets.map((bucket) => buckets.push({ [bucket.id]: bucket.qty }));

    let bucketIds = Object.keys(Object.assign({}, ...buckets));

    bucketIds.map((id) => {
      response.transactions.sale.map((trans) => {
        if (trans.bucketid === Number(id)) {
          transactionArr.push({ ...trans, type: "sale" });
        }
        return true;
      });
      response.transactions.purchase.map((trans) => {
        if (trans.bucketid === Number(id)) {
          transactionArr.push({ ...trans, type: "purchase" });
        }
        return true;
      });
      return true;
    });
    let groupedObj = groupByKey(transactionArr, "bucketid");

    buckets.map((bucket, index) => {
      let id = bucketIds[index];
      let qty = bucket[id];
      let barData = [];
      if (groupedObj[id]) {
        let sortedArr = groupedObj[id].sort(
          (a, b) => Date.parse(b.date) - Date.parse(a.date)
        );
        sortedArr.map((arr) => {
          if (arr.type === "sale") {
            qty += Number(arr.qty);
            barData.push({ ...arr, qty: qty });
          } else {
            qty -= Number(arr.qty);
            barData.push({ ...arr, qty: qty });
          }
          return true;
        });
        groupedObj[id] = barData.reverse();
        barData = [];
      } else {
        groupedObj[id] = { bucketid: id, qty: bucket[id], date: new Date() };
      }
      return true;
    });
    setChartData(groupedObj);
    setBucketIds(bucketIds);
  };

  const handleSearch = () => {
    let data = {
      bucketids: fullOptionToValue(bucket),
      from: dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z",
    };
    DashboardProxy.GetBucketTransactions(data, (response) => {
      if (response) {
        handleResponse(response);
      } else {
        setErrorSnackbar(true);
      }
    });
  };
  return (
    <div>
      <CommonSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Failed"}
        handleClose={() => setErrorSnackbar(false)}
      />
      <Paper elevation={4}>
        <Card>
          <CardHeader
            avatar={<GrTransaction size={28} color="#8884D8" />}
            title={
              <Typography
                component="span"
                fontWeight={Constants.defaultFontWeight}
                fontSize={"1.25rem"}
              >
                Bucket Transactions
              </Typography>
            }
            subheader="Bucket transactions by date"
          />
          <CardContent>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                mb: 3,
                mt: 2,
                flexWrap: "wrap",
                justifyContent: "space-evenly",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  minWidth: "150px",
                  maxWidth: "auto",
                  gap: "20px",
                }}
              >
                <Box sx={{ alignSelf: "center", mt: 1, width: "65px" }}>
                  <Typography>Bucket</Typography>
                </Box>
                <Box
                  sx={{
                    alignSelf: "center",
                    minWidth: "150px",
                    maxWidth: "450px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    isSearchable={true}
                    name="bucket"
                    closeMenuOnSelect={false}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    isMulti
                    options={bucketOptions}
                    value={bucket}
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    onChange={(selectedOption) => setBucket(selectedOption)}
                  />
                </Box>
                <Box display={"flex"} gap="15px" ml={3}>
                  <Typography mt={2}>From</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      ampm={true}
                      renderInput={(props) => (
                        <TextField
                          fullWidth
                          {...props}
                          inputProps={{ ...props.inputProps, readOnly: true }}
                        />
                      )}
                      value={fromDate}
                      inputFormat="DD/MM/YYYY"
                      onChange={(newValue) => {
                        // Get the current time
                        const now = dayjs();

                        // Set startOfMonth to the first day of the selected month, but with the current system time
                        const fromDate = dayjs(newValue)
                          .hour(now.hour())
                          .minute(now.minute())
                          .second(now.second())
                          .millisecond(now.millisecond())
                          .toDate();

                        setFromDate(fromDate);
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
              <LoadingButton
                // loading={userReportListLoader}
                loadingPosition="start"
                variant="contained"
                onClick={() => handleSearch(bucket, fromDate)}
                startIcon={<SearchOutlined size="large" />}
                sx={{ mt: 1 }}
                disabled={bucketOptions.length === 0}
              >
                Search
              </LoadingButton>
            </Box>
            <Box height={matches ? 300 : 700} width={matches ? 320 : "45vw"}>
              <ResponsiveContainer height={700}>
                <LineChart>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {bucketIds.map((id) => (
                    <Line
                      key={id}
                      type="monotone"
                      data={chartData[id]}
                      dataKey="qty"
                      name={id}
                      stroke={`#8884d8`}
                      activeDot={{ r: 8 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </div>
  );
}
